import {
  Box,
  Divider,
  Icon,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'
import { ITenantBalance } from '../../../../../core/types/Tenant'
import { BalanceDarkCard } from '../../../../settings/TenantBalanceWithdraw/BalanceDarkCard'
import { useCallback, useEffect, useRef, useState } from 'react'
import { BalanceLightCard } from '../../../../settings/TenantBalanceWithdraw/BalanceLightCard'
import { WithdrawLimitInfo } from '../../../../settings/TenantBalanceWithdraw/WithdrawLimitInfo'
import { TenantController } from '../../../../../core/controllers/TenantController'
import { IErrorResponse } from '../../../../../core/types/ErrorResponse'
import InfoTooltip from '../../../../../components/Tooltip/InfoTooltip'
import UserSettingExtractDownloadButton from '../components/UserSettingExtractDownloadButton'
import { useAdminUserContext } from '../../../../../core/hooks/useAdminUserContext'
import UserSettingExtractTransferButton from '../components/UserSettingExtractTransfer/UserSettingExtractTransferButton'
import ErrorSnackbar from '../../../../../components/Snackbar/ErrorSnackbar'
import AdminController from '../../../../../core/controllers/AdminController'
import {
  AdminTransferItem,
  AdminTransferResponse,
} from '../../../../../core/types/Admin'
import UserSettingExtractTransferItemMobile from '../components/UserSettingExtractTransfer/UserSettingExtractTransferItemMobile'
import ContentDialog from '../../../../../components/Dialogs/ContentDialog'
import UserSettingExtractTransferQrCode from '../components/UserSettingExtractTransfer/UserSettingExtractTransferQrCode'
import { set } from 'lodash'
import { AsaasSeal } from '../../../../../components/AsaasSeal/AsaasSeal'

const UserSettingExtract = () => {
  const { tenantSettings } = useAdminUserContext()

  const [data, setData] = useState<ITenantBalance | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [transfers, setTransfers] = useState<AdminTransferItem[]>([])
  const [transferData, setTransferData] =
    useState<AdminTransferResponse | null>(null)

  const oldId = useRef('')

  const handleChangePreviewWithdraw = useCallback(async () => {
    setLoading(true)
    try {
      if (!tenantSettings) {
        setError('Usuário não encontrado')
        return
      }

      const [responseBalance, responseTransfers] = await Promise.all([
        TenantController.getBalance({
          tenantId: tenantSettings.tenantId,
        }),
        AdminController.getTransfersByTenantId({
          tenantId: tenantSettings.tenantId,
        }),
      ])

      const balanceError = responseBalance as IErrorResponse
      const balanceData = responseBalance as ITenantBalance

      if (balanceError.code) {
        setError(balanceError.error)
      } else {
        setData(balanceData)
      }

      if (responseTransfers.error) {
        setError(balanceError.error)
      } else {
        setTransfers(responseTransfers.data ?? [])
      }
    } finally {
      setLoading(false)
    }
  }, [tenantSettings, setLoading, setData, setError, setTransfers])

  useEffect(() => {
    if (oldId.current !== tenantSettings?.tenantId) {
      oldId.current = tenantSettings?.tenantId ?? ''
      handleChangePreviewWithdraw()
    }
  }, [])

  return (
    <>
      <Stack direction="column" spacing={0} sx={{ py: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="end"
          sx={{ p: 1 }}
        >
          <UserSettingExtractTransferButton
            onRefresh={handleChangePreviewWithdraw}
          />
          <UserSettingExtractDownloadButton
            tenantId={tenantSettings?.tenantId ?? ''}
          />
        </Stack>
        <ListItemDefault
          title=""
          widthTitlePercentage={0.01}
          variant="compact"
          direction="column"
          value={
            <>
              {!loading && (
                <Box width="100%">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BalanceDarkCard
                      balance={data?.commissionsReceived ?? 0}
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box>Recebidas</Box>
                          <InfoTooltip title="Total de comissões recebidas (pagas)" />
                        </Stack>
                      }
                    />
                    <Divider orientation="vertical" flexItem />
                    <BalanceLightCard
                      balance={data?.indicatedAmount ?? 0}
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box>Indicação</Box>
                          <InfoTooltip title="Total de comissões de indicação recebidas" />
                        </Stack>
                      }
                    />
                  </Stack>
                  <Divider />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BalanceDarkCard
                      balance={data?.transferredAmount ?? 0}
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box>Total sacado</Box>
                          <InfoTooltip title="Total sacado" />
                        </Stack>
                      }
                    />
                    <Divider orientation="vertical" flexItem />
                    <BalanceLightCard
                      balance={data?.availableAmount ?? 0}
                      title={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box>Saldo disponível</Box>
                          <InfoTooltip title="Saldo disponível pra saque" />
                        </Stack>
                      }
                    />
                  </Stack>
                  <WithdrawLimitInfo
                    withdrawLimitEnabled={data?.withdrawLimitEnabled ?? false}
                    withdrawLimit={data?.withdrawLimit ?? null}
                    withdrawLimitRemaining={data?.withdrawLimitRemaining ?? null}
                  />
                </Box>
              )}
              {loading && (
                <Stack spacing={1} sx={{ p: 1 }}>
                  <Skeleton variant="rounded" width="100%" height="95px" />
                  <Skeleton variant="rounded" width="100%" height="95px" />
                </Stack>
              )}
            </>
          }
        />

        <Stack direction="column" spacing={1} sx={{ p: 1, mt: 1 }}>
          <Typography
            sx={{
              display: 'flex',
              fontWeight: '600',
              fontSize: '16px',
              lineHeight: '20px',
            }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            <Icon sx={{ mr: 1 }}>
              <img src="/assets/icons/history/historyIcon.svg" alt="history" />
            </Icon>
            Histórico de transferências
            <Tooltip title="Histórico de transferências realizadas manualmente para o usuário">
              <Icon sx={{ ml: 1 }}>
                <img src="/assets/icons/help/helpIcon.svg" alt="help" />
              </Icon>
            </Tooltip>
          </Typography>

          <Stack>
            {transfers.map((item, index) => (
              <UserSettingExtractTransferItemMobile
                key={item.id}
                item={item}
                index={index}
                handleShowDetails={() =>
                  setTransferData({
                    pixQrCode: item.pixQrCode,
                    pixUrl: item.pixQrUrl,
                  })
                }
              />
            ))}
          </Stack>
        </Stack>
      </Stack>

      <ContentDialog
        maxWidth="xs"
        title="Dados para transferência"
        open={transferData !== null}
        onClose={() => setTransferData(null)}
      >
        {transferData !== null && (
          <UserSettingExtractTransferQrCode data={transferData} />
        )}
      </ContentDialog>

      <ErrorSnackbar open={error !== ''} onClose={() => setError('')}>
        {error}
      </ErrorSnackbar>

      <AsaasSeal sx={{ mt: 3 }} />
    </>
  )
}

export default UserSettingExtract
